import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import { AlertTriageIcon, CrossIcon } from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { Typo } from "../../Typo";
import { withSupportLink } from "./supportLink";

type NoRebateAccountModalProps = {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
};

/**
 * Blocking modal shown when the Master IB tries to create a rebate program while
 * **their own** rebate account isn't active — missing, not yet provisioned, or
 * unavailable for any other reason. The canvas's copy is deliberately a catch-all
 * (PM-confirmed) rather than naming a cause.
 *
 * Distinct from the lookup gate's `no_rebate_account` error, which is about the
 * *partner's* account. This one blocks the flow before it starts, and unlike the
 * program-cap modal there is nothing to self-serve — support is the only path, so
 * the single action just dismisses.
 */
export const NoRebateAccountModal = ({
  open,
  onClose,
  isMobile = false,
}: NoRebateAccountModalProps) => {
  const { t } = useTranslation();
  const closeLabel = t("common_close", "Close");

  const closeButton = (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={onClose}
      className={clsx(isMobile && "w-full")}
      data-testid="no_rebate_account_modal_close_button"
    >
      {closeLabel}
    </ButtonV2>
  );

  const illustration = (
    <AlertTriageIcon
      className="h-16 w-16"
      aria-hidden="true"
      focusable="false"
    />
  );

  const title = t(
    "subIBReport_noRebateAccountTitle",
    "No active rebate account",
  );

  const body = withSupportLink(
    t(
      "subIBReport_noRebateAccountBody",
      "You don't have an active rebate account at the moment. Please contact support@pepperstonepartners.com.",
    ),
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={onClose}
        isFullScreen
        isDismissable={false}
        showCloseButton
        closeButtonTestId="no_rebate_account_modal_dismiss_button"
        contentContainerClassName="py-3"
        footer={closeButton}
      >
        <div
          className="flex flex-col gap-4 pb-1"
          role="alert"
          data-testid="no_rebate_account_modal"
        >
          {illustration}
          <Typo element="h2" type="headingLG" textColor="text-text-primary">
            {title}
          </Typo>
          <Typo element="p" type="bodySM" textColor="text-text-secondary">
            {body}
          </Typo>
        </div>
      </BottomSheet>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      headerClassName="!hidden"
      // `!` beats ResponsiveModal's hardcoded `mt-6`.
      footerContainerClassName="!mt-8"
      footer={<div className="flex justify-end">{closeButton}</div>}
    >
      {/* Hiding the modal header also hides ResponsiveModal's own X, so the
          dismiss affordance is rendered here. */}
      <div
        className="flex flex-col gap-4"
        role="alert"
        data-testid="no_rebate_account_modal"
      >
        <div className="flex flex-col">
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={closeLabel}
              onClick={onClose}
              data-testid="no_rebate_account_modal_dismiss_button"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {illustration}
        </div>
        <Typo element="h2" type="headingXL" textColor="text-text-primary">
          {title}
        </Typo>
        <Typo element="p" type="bodyMD" textColor="text-text-secondary">
          {body}
        </Typo>
      </div>
    </ResponsiveModal>
  );
};
